import React from "react";
import api from "@/lib/api";
import {
  X, Trash, PaperPlaneRight, Robot, User as UserIcon, Paperclip,
  CheckCircle, Gear, ClipboardText, MagnifyingGlass, ArrowSquareOut,
  FilePdf, FileXls, WarningCircle, Sparkle,
  SquaresFour, ChatCircleText, Clock, CaretRight, ArrowLeft,
  CurrencyDollar, Receipt, ArrowClockwise,
  DownloadSimple, UsersThree, PencilSimple, ArrowUUpLeft, Prohibit, Plus, Paperclip as PaperclipIcon,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import { useAuth } from "@/context/AuthContext";
import WizardFields from "@/components/erp/WizardFields";

const AGENT_KEY = "purchase-req-create";
const BRAND = "#0020C2";
const BASE = `/erp-agents/${AGENT_KEY}`;

const THINKING = [
  "Validating references against Oracle…",
  "Resolving business unit…",
  "Checking the deliver-to location…",
  "Running pre-flight validation…",
  "Assembling the requisition draft…",
];

const lineAmount = (ln) =>
  ln?.quantity_based === false ? Number(ln?.amount || 0) : Number(ln?.price || 0) * Number(ln?.quantity || 0);
function draftTotal(draft) {
  const lines = draft?.lines || [];
  return { sum: lines.reduce((a, ln) => a + lineAmount(ln), 0), ccy: lines[0]?.currency_code || "" };
}

// Fetch a protected file through axios (so the Bearer token is sent) and hand it to
// the browser. `inline` opens it in a new tab (PDF preview); otherwise it downloads.
// Used by BOTH the chat PDF card and the dashboard so viewing a PDF works everywhere.
async function downloadViaApi(path, filename, { inline = false } = {}) {
  const { data } = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  if (inline) {
    window.open(url, "_blank");
  } else {
    const a = document.createElement("a");
    a.href = url; a.download = filename || "download";
    document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/* ── parsed-document verification card (shown after a PDF/Word upload) ───────── */
function DetectChips({ label, values }) {
  if (!values || !values.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mb-1">
      <span className="text-[10px] uppercase tracking-wide text-gray-400 mr-1">{label}</span>
      {values.map((v, i) => (
        <span key={i} className="text-[11px] font-mono bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 text-gray-700">{v}</span>
      ))}
    </div>
  );
}
function ParsedDocCard({ parsed, fileName, busy, onCreateNew, onAttachExisting }) {
  const d = parsed?.detected || {};
  const nothing = !d.amounts?.length && !d.dates?.length && !d.emails?.length && !d.doc_numbers?.length;
  return (
    <div className="border-2 rounded-xl bg-white shadow-md p-3.5" style={{ borderColor: `${BRAND}40` }}>
      <div className="flex items-center gap-2 mb-2">
        <FilePdf size={16} weight="fill" className="text-red-500" />
        <span className="text-[13px] font-semibold text-gray-900">We read “{parsed?.file_name || fileName}” — please check the details</span>
      </div>
      <div className="mb-2">
        <DetectChips label="Amounts" values={d.amounts} />
        <DetectChips label="Dates" values={d.dates} />
        <DetectChips label="Doc no." values={d.doc_numbers} />
        <DetectChips label="Emails" values={d.emails} />
        {nothing && <div className="text-[11px] text-gray-400">No specific amounts/dates detected — check the extracted text below.</div>}
      </div>
      {parsed?.preview && (
        <div className="max-h-32 overflow-auto border border-gray-100 rounded-lg p-2 bg-gray-50 text-[11px] text-gray-600 whitespace-pre-wrap mb-3">
          {parsed.preview}{parsed.char_count > parsed.preview.length ? "…" : ""}
        </div>
      )}
      <p className="text-[11px] text-gray-500 mb-2">If these look right, choose what to do with it. You can correct anything in chat afterwards.</p>
      <div className="flex gap-2">
        <button disabled={busy} onClick={onCreateNew} className="flex-1 text-[12px] font-medium text-white py-2 rounded-lg disabled:opacity-50" style={{ background: BRAND }}>Create a PR from this</button>
        <button disabled={busy} onClick={onAttachExisting} className="flex-1 text-[12px] font-medium text-gray-700 py-2 rounded-lg border border-gray-300 disabled:opacity-50">Attach to an existing PR</button>
      </div>
    </div>
  );
}

/* ── embedded PDF viewer (Feature E) — fetches the viewPDF blob WITH auth and shows
   it inline in an <iframe>. Used identically by the chat card and the dashboard so
   "View PDF" embeds the document in-place rather than opening a blank tab. ───────── */
function PdfViewer({ path, filename, height = 520 }) {
  const [url, setUrl] = React.useState(null);
  const [err, setErr] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancel = false; let objUrl = null;
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get(path, { responseType: "blob" });
        if (cancel) return;
        objUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        setUrl(objUrl);
      } catch (e) {
        if (!cancel) setErr(e?.response?.status === 502
          ? "Oracle couldn't generate the PDF for this requisition."
          : (e?.response?.status === 404 ? "Requisition not found." : "Couldn't load the PDF."));
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; if (objUrl) setTimeout(() => URL.revokeObjectURL(objUrl), 2000); };
  }, [path]);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
      <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-gray-100 bg-white">
        <span className="text-[11px] text-gray-500 flex items-center gap-1.5"><FilePdf size={13} className="text-red-500" /> {filename || "Requisition PDF"}</span>
        {url && <a href={url} download={filename || "requisition.pdf"} className="text-[11px] text-[#0020C2] hover:underline flex items-center gap-1"><DownloadSimple size={12} /> Download</a>}
      </div>
      {loading ? <div className="flex items-center justify-center text-[12px] text-gray-400" style={{ height }}>Generating PDF from Oracle…</div>
        : err ? <div className="flex items-center justify-center text-[12px] text-red-600 px-4 text-center" style={{ height }}>{err}</div>
        : <iframe title={filename || "Requisition PDF"} src={url} className="w-full bg-white" style={{ height, border: 0 }} />}
    </div>
  );
}

/* ── chat PDF card (Feature E) — embeds the PDF inline in the conversation ──────── */
function PdfCard({ info }) {
  const [open, setOpen] = React.useState(true);   // embed immediately when offered
  const id = info?.requisition_header_id;
  const num = info?.requisition || id;
  return (
    <div className="border rounded-xl bg-white shadow-sm p-3 w-full" style={{ borderColor: `${BRAND}30` }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[12px] text-gray-800"><FilePdf size={15} weight="fill" className="text-red-500" /> Requisition {num} PDF</div>
        <button onClick={() => setOpen((o) => !o)} className="text-[11px] text-[#0020C2] hover:underline">{open ? "Hide" : "View"}</button>
      </div>
      {open && <PdfViewer path={`${BASE}/pdf/${id}`} filename={`requisition_${num}.pdf`} height={460} />}
    </div>
  );
}

/* ── chat bubble ─────────────────────────────────────────────────────────── */
function Bubble({ msg, phraseIdx }) {
  const isUser = msg.role === "user";
  const assistantCls = msg.tone === "error" ? "bg-red-50 border border-red-300 text-red-800"
    : msg.tone === "success" ? "bg-emerald-50 border border-emerald-300 text-emerald-800"
    : "bg-white border border-gray-200 text-gray-900";
  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white"
        style={{ background: isUser ? BRAND : msg.tone === "error" ? "#DC2626" : msg.tone === "success" ? "#059669" : "#374151" }}>
        {isUser ? <UserIcon size={11} weight="bold" /> : msg.tone === "error" ? <WarningCircle size={11} weight="fill" /> : msg.tone === "success" ? <CheckCircle size={11} weight="fill" /> : <Robot size={11} weight="bold" />}
      </div>
      <div className={`max-w-[85%] px-3 py-2 text-[13px] leading-relaxed rounded-2xl ${isUser ? "text-white rounded-tr-sm" : `${assistantCls} shadow-sm rounded-tl-sm`}`}
        style={isUser ? { background: BRAND } : undefined}>
        {msg.loading ? (
          <div className="flex items-center gap-2 py-0.5">
            <div className="flex items-center gap-1">{[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full animate-bounce bg-gray-400" style={{ animationDelay: `${d}ms` }} />)}</div>
            <span className="text-[11px] text-gray-400">{THINKING[phraseIdx % THINKING.length]}</span>
          </div>
        ) : <p className="whitespace-pre-wrap break-words">{msg.text}</p>}
      </div>
    </div>
  );
}

/* ── inline field card (next_field_ui) ───────────────────────────────────── */
// `extraParams` (e.g. {project_id: 123} or {bu_id: 456}) scopes a dependent
// dropdown to valid values only — mirrors the platform's supplier_site-by-supplier
// pattern for project_task-by-project and projects-by-BU (Feature B fine-tuning).
function LookupSelect({ lookup, onPick, extraParams }) {
  const [q, setQ] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [note, setNote] = React.useState("");
  const extraKey = extraParams ? JSON.stringify(extraParams) : "";
  React.useEffect(() => {
    let cancel = false;
    const t = setTimeout(async () => {
      setLoading(true); setNote("");
      try {
        const { data } = await api.get(`/erp-agents/lookups/${lookup}`, { params: { q, limit: 20, ...(extraParams || {}) } });
        if (cancel) return;
        setRows(data.rows || []);
        if (data.status && data.status !== "ok") setNote(
          data.status === "unconfigured" ? "Connect your Oracle profile to load options."
          : data.status === "auth_error" ? "Oracle sign-in failed — check your credentials."
          : data.message || "Oracle couldn't return options right now.");
      } catch { if (!cancel) setNote("Couldn't load options."); }
      finally { if (!cancel) setLoading(false); }
    }, 220);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, lookup, extraKey]);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-gray-100">
        <MagnifyingGlass size={13} className="text-gray-400" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full text-[13px] outline-none" />
      </div>
      {note && <div className="px-2.5 py-1.5 text-[11px] text-amber-700 bg-amber-50">{note}</div>}
      <div className="max-h-44 overflow-y-auto">
        {loading && <div className="px-2.5 py-2 text-[11px] text-gray-400">Searching Oracle…</div>}
        {!loading && rows.length === 0 && !note && <div className="px-2.5 py-2 text-[11px] text-gray-400">No matches.</div>}
        {rows.map((r) => (
          <button key={String(r.value)} onClick={() => onPick(r)} className="w-full text-left px-2.5 py-1.5 text-[13px] hover:bg-blue-50 border-b border-gray-50 last:border-0">
            <div className="text-gray-900">{r.label}</div>
            {r.sub && <div className="text-[10px] font-mono text-gray-400">{r.sub}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

// Lightweight per-field suggestion memory (this browser session only) — a lot
// of these text fields (description, justification, requisition_id…) have no
// Oracle-backed lookup, so recently-typed values are the best available
// autocomplete source without inventing data.
const RECENT_KEY_PREFIX = "pr_field_recent:";
function recentValues(field) {
  try { return JSON.parse(sessionStorage.getItem(RECENT_KEY_PREFIX + field) || "[]"); } catch { return []; }
}
function rememberValue(field, val) {
  if (!val) return;
  try {
    const cur = recentValues(field).filter((v) => v !== val);
    cur.unshift(val);
    sessionStorage.setItem(RECENT_KEY_PREFIX + field, JSON.stringify(cur.slice(0, 8)));
  } catch { /* sessionStorage unavailable — no autocomplete history, not fatal */ }
}

// Free-text field input with autocomplete: item_number gets live suggestions
// from the Oracle item catalog (same lookup the "item" dropdown uses); every
// other text field falls back to this session's own recently-typed values.
function TextFieldWithSuggestions({ fieldUi, val, setVal, onSubmit }) {
  const [open, setOpen] = React.useState(false);
  const [itemHits, setItemHits] = React.useState([]);
  const isItemNumber = fieldUi.field === "item_number";
  const recents = React.useMemo(() => recentValues(fieldUi.field), [fieldUi.field]);

  React.useEffect(() => {
    if (!isItemNumber || !val.trim()) { setItemHits([]); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/erp-agents/lookups/items", { params: { q: val.trim(), limit: 8 } });
        if (!cancel) setItemHits(data.rows || []);
      } catch { if (!cancel) setItemHits([]); }
    }, 220);
    return () => { cancel = true; clearTimeout(t); };
  }, [val, isItemNumber]);

  const suggestions = isItemNumber
    ? itemHits.map((r) => ({ value: r.label, hint: r.sub }))
    : recents.filter((v) => !val || v.toLowerCase().includes(val.toLowerCase())).map((v) => ({ value: v }));

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <input type={fieldUi.uiType === "number" ? "number" : fieldUi.uiType === "date" ? "date" : "text"}
          value={val} onChange={(e) => setVal(e.target.value)} placeholder={fieldUi.placeholder || ""}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); }}
          className="flex-1 text-[13px] border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#0020C2]" />
        <button onClick={onSubmit} className="text-[12px] text-white px-3 py-1.5 rounded-lg" style={{ background: BRAND }}>Send</button>
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-md max-h-40 overflow-y-auto">
          {suggestions.slice(0, 8).map((s, i) => (
            <button key={i} type="button" onMouseDown={() => setVal(s.value)}
              className="w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-blue-50 border-b border-gray-50 last:border-0">
              {s.value}{s.hint ? <span className="text-[10px] text-gray-400 ml-1">{s.hint}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// `lookupContext` carries the most recently picked business_unit/project ids so a
// dependent field (project_task ← project; project ← business_unit) can scope its
// dropdown to valid values only. `onRawPick` lets the parent capture the raw
// {value,label} row (not just the composed chat string) to update that context.
function FieldCard({ fieldUi, disabled, onSubmit, onSkip, onRawPick, lookupContext }) {
  const [val, setVal] = React.useState("");
  const isLookup = fieldUi.uiType === "searchable_dropdown" && fieldUi.lookup;
  const submitPlain = () => {
    if (val === "") return;
    rememberValue(fieldUi.field, val);
    onSubmit(`${fieldUi.label}: ${val}`);
  };
  const extraParams =
    fieldUi.field === "project_task" ? (lookupContext?.projectId ? { project_id: lookupContext.projectId } : undefined)
    : fieldUi.field === "project" ? (lookupContext?.buId ? { bu_id: lookupContext.buId } : undefined)
    : undefined;
  const missingParent = fieldUi.field === "project_task" && !lookupContext?.projectId;
  return (
    <div className={`border rounded-xl bg-white shadow-sm p-3 ${disabled ? "opacity-60" : ""}`} style={{ borderColor: `${BRAND}30` }}>
      <div className="text-[11px] font-semibold text-gray-700 mb-2">{fieldUi.label}{fieldUi.required && <span className="text-red-500 ml-0.5">*</span>}</div>
      {disabled ? <div className="text-[11px] text-gray-400 italic">Answered.</div>
        : missingParent ? <div className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2.5 py-2">Pick a project first — tasks are scoped to it.</div>
        : isLookup ? <LookupSelect lookup={fieldUi.lookup} extraParams={extraParams}
            onPick={(r) => { onRawPick?.(fieldUi.field, r); onSubmit(`${fieldUi.label}: ${r.label} [id=${r.value}]`); }} />
        : fieldUi.uiType === "dropdown" ? (
          <div className="flex flex-col gap-1">{(fieldUi.options || []).map((o) => <button key={o.value} onClick={() => onSubmit(`${fieldUi.label}: ${o.value}`)} className="text-left text-[13px] px-2.5 py-1.5 border border-gray-200 rounded-lg hover:bg-blue-50">{o.label}</button>)}</div>
        ) : (
          <TextFieldWithSuggestions fieldUi={fieldUi} val={val} setVal={setVal} onSubmit={submitPlain} />
        )}
      {!disabled && <button onClick={onSkip} className="mt-2 text-[11px] text-gray-400 hover:text-gray-600">Skip / answer in chat</button>}
    </div>
  );
}

// Field *derivation* for PR's ConfirmReviewPopup — maps the staged draft object
// straight onto the {label, value} shape the shared popup renders. This stays
// local to this file: ConfirmReviewPopup itself has no knowledge of `draft`.
function prDraftToFields(draft) {
  return [
    { label: "Business unit", value: draft?.requisitioning_bu_name || draft?.requisitioning_bu_id || "—" },
    { label: "Preparer", value: draft?.preparer_name || draft?.preparer_email || draft?.preparer_id || "—" },
    { label: "Description", value: draft?.description || "—" },
  ];
}

// Lines + total breakdown, rendered in ConfirmReviewPopup's `children` slot
// (below the field grid, above Confirm/Cancel) — same content/markup the old
// inline ConfirmCard used to show, just relocated into the shared popup.
function PrLinesBreakdown({ draft }) {
  const { sum, ccy } = draftTotal(draft);
  return (
    <div className="mb-5">
      <div className="border border-gray-100 rounded-lg overflow-hidden mb-2.5">
        {(draft?.lines || []).map((ln, i) => (
          <div key={i} className="px-2.5 py-1.5 text-[11px] border-b border-gray-50 last:border-0">
            <div className="font-medium text-gray-900">{ln.line_number}. {ln.item_description || (ln.item_id ? `Item ${ln.item_id}` : "Line")}</div>
            <div className="text-gray-500">{ln.quantity_based === false ? `${ln.amount} ${ln.currency_code} (fixed price)` : `${ln.quantity} ${ln.uom_code || "EA"} × ${ln.price} ${ln.currency_code} = ${lineAmount(ln).toFixed(2)} ${ln.currency_code}`}</div>
            <div className="text-gray-400">{ln.destination_type_code} · to {ln.deliver_to_location_code || ln.deliver_to_location_id || "—"} · by {ln.requested_delivery_date || "—"}</div>
            {ln.department_name && <div className="text-gray-400">Department: {ln.department_name}</div>}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between"><span className="text-[11px] text-gray-400">Total</span><span className="text-[13px] font-semibold text-gray-900">{sum.toFixed(2)} {ccy}</span></div>
    </div>
  );
}

// Inline chat card version of the confirm/review step — rolled back from a
// full-screen popup per request; same fields/lines/buttons, embedded in the
// chat stream like every other card (FieldCard, PostCreateCard, BatchCard...).
function ConfirmCard({ draft, busy, onConfirm, onKeepEditing }) {
  return (
    <div className="border border-gray-200 rounded-xl bg-white p-3.5 max-w-[540px] shadow-sm">
      <div className="text-[13px] font-bold text-gray-900 mb-2.5">Review Purchase Requisition</div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {prDraftToFields(draft).map((f, i) => (
          <div key={i} className="bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-0.5">{f.label}</p>
            <p className="text-[12px] font-medium text-gray-900 break-words">{f.value}</p>
          </div>
        ))}
      </div>
      <PrLinesBreakdown draft={draft} />
      <div className="flex gap-2 mt-3">
        <button onClick={onConfirm} disabled={busy}
          className="flex-1 py-2 rounded-lg font-bold text-white text-[12px] disabled:opacity-50"
          style={{ background: "linear-gradient(135deg, #10B981, #059669)" }}>
          ✓ Submit to Oracle
        </button>
        <button onClick={onKeepEditing} disabled={busy}
          className="flex-1 py-2 rounded-lg font-semibold text-[12px] border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-50">
          ✕ Keep Editing
        </button>
      </div>
    </div>
  );
}

function ExcelTable({ records }) {
  const rows = records || [];
  if (!rows.length) return <div className="text-[11px] text-gray-400">No rows parsed.</div>;
  const cols = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] border-collapse">
        <thead><tr>{cols.map((c) => <th key={c} className="text-left px-2 py-1 border-b border-gray-200 text-gray-500 font-medium whitespace-nowrap">{c.replace(/_/g, " ")}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-gray-50">{cols.map((c) => <td key={c} className="px-2 py-1 text-gray-800 whitespace-nowrap">{r[c] ?? ""}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

// Minimal chip — the parsed content is kept server-side and NOT dumped here.
function FileChip({ name, url, fkind }) {
  const Icon = fkind === "excel" ? FileXls : FilePdf;
  return (
    <div className="inline-flex items-center gap-2 border border-gray-200 rounded-lg bg-white px-2.5 py-1.5 text-[12px] shadow-sm">
      <Icon size={15} weight="fill" className={fkind === "excel" ? "text-emerald-600" : "text-red-500"} />
      <span className="text-gray-700 truncate max-w-[220px]">{name}</span>
      {url && <button onClick={() => window.open(url, "_blank")} title="Open document" className="text-gray-400 hover:text-gray-700"><ArrowSquareOut size={13} /></button>}
    </div>
  );
}

function BatchCard({ batch, busy, onApprove, onCancel }) {
  // classification: "line_append" | "single_pr" | "single_pr_multi_line" |
  // "multiple_prs" — each PR/requisition's line count is in lines_per_pr, e.g.
  // [3, 1, 2], so a multi-line PR is visibly distinct from several single-line PRs.
  const linesPerPr = batch.lines_per_pr || [];
  const prCount = batch.pr_count ?? linesPerPr.length;
  const lineCount = batch.line_count ?? batch.record_count;
  const label = {
    line_append: `lines appended to ${prCount} existing requisition${prCount === 1 ? "" : "s"}`,
    single_pr: "1 purchase requisition",
    single_pr_multi_line: `1 purchase requisition with ${lineCount} line(s)`,
    multiple_prs: `${prCount} purchase requisitions (${lineCount} line(s) total)`,
  }[batch.classification] || `${batch.record_count} record(s)`;
  const unit = batch.classification === "line_append" ? "Req" : "PR";
  const breakdown = linesPerPr.length > 1
    ? linesPerPr.map((n, i) => `${unit} ${i + 1}: ${n} line${n === 1 ? "" : "s"}`).join(" · ")
    : "";
  return (
    <div className="border-2 rounded-xl bg-white shadow-sm p-3" style={{ borderColor: `${BRAND}30` }}>
      <div className="flex items-center gap-2 mb-1"><ClipboardText size={15} style={{ color: BRAND }} /><span className="text-[13px] font-semibold text-gray-900">Batch detected</span></div>
      <p className="text-[11px] text-gray-600 mb-2">{batch.message}</p>
      <p className="text-[12px] text-gray-800 mb-1">Will process <b>{label}</b>.</p>
      {breakdown && <p className="text-[11px] text-gray-500 mb-2">{breakdown}</p>}
      <div className="max-h-40 overflow-auto mb-3 border border-gray-100 rounded-lg p-2"><ExcelTable records={batch.records} /></div>
      <div className="flex gap-2">
        <button disabled={busy} onClick={onApprove} className="flex-1 text-[12px] font-medium text-white py-2 rounded-lg disabled:opacity-50" style={{ background: BRAND }}>Approve batch</button>
        <button disabled={busy} onClick={onCancel} className="flex-1 text-[12px] font-medium text-gray-600 py-2 rounded-lg border border-gray-200 disabled:opacity-50">Cancel</button>
      </div>
    </div>
  );
}

function BatchResultCard({ results, created, total }) {
  return (
    <div className="border-2 rounded-xl bg-white shadow-sm p-3" style={{ borderColor: created === total ? "#059669" : `${BRAND}30` }}>
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle size={15} weight="fill" style={{ color: created === total ? "#059669" : BRAND }} />
        <span className="text-[13px] font-semibold text-gray-900">Batch complete — {created} of {total} succeeded</span>
      </div>
      <div className="space-y-1.5 max-h-72 overflow-auto">
        {results.map((r, i) => {
          // "line_append" mode results carry requisition_id/lines_attempted/
          // lines_appended/errors; "new_prs" mode results carry pr/requisition_number/
          // requisition_header_id/lines/business_unit/error — render each accordingly.
          const isAppend = r.requisition_id !== undefined;
          return (
            <div key={i} className={`flex items-start gap-2 text-[12px] px-2.5 py-1.5 rounded-lg border ${r.success ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
              {r.success
                ? <CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                : <WarningCircle size={13} weight="fill" className="text-red-500 mt-0.5 shrink-0" />}
              <div className="min-w-0">
                {isAppend ? (
                  <>
                    <div className="font-medium text-gray-900">Requisition {r.requisition_id}</div>
                    <div className="text-gray-500 break-words">
                      {r.success
                        ? `${r.lines_appended}/${r.lines_attempted} line(s) appended`
                        : (r.errors?.filter(Boolean).join("; ") || "Failed")}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium text-gray-900">
                      PR {r.pr ?? i + 1}{r.business_unit ? ` · ${r.business_unit}` : ""}
                      {r.success && r.requisition_number ? ` → ${r.requisition_number}` : ""}
                    </div>
                    <div className="text-gray-500 break-words">
                      {r.success
                        ? `${r.lines ?? 1} line(s)${r.requisition_header_id ? ` · id ${r.requisition_header_id}` : ""} · verified in Oracle`
                        : (r.error || r.likely_cause || "Rejected")}
                    </div>
                    {r.success && (r.charge_account_status || r.funds_status) && (
                      <div className="text-[10px] mt-0.5 flex flex-wrap gap-x-2">
                        {r.charge_account_status && (
                          <span className={r.needs_charge_account ? "text-amber-600" : "text-gray-400"}>
                            charge account: {r.needs_charge_account ? "needs attention" : "set"}
                          </span>
                        )}
                        {r.funds_status && (
                          <span className={r.funds_passed === false ? "text-[#DC2626]" : "text-gray-400"}>
                            funds: {r.funds_status}
                          </span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── composer ────────────────────────────────────────────────────────────── */
// Quick-start prompts shown when the box is empty, plus this session's own
// recent messages when the user has typed something — real, working
// suggestions rather than a static list only.
const QUICK_PROMPTS = [
  "Create a new requisition for…",
  "Attach this to requisition …",
  "List available business units",
  "Show my recent requisitions",
];

function Composer({ value, onChange, onSend, onAttach, busy, placeholder, history }) {
  const [showSuggest, setShowSuggest] = React.useState(false);
  const suggestions = React.useMemo(() => {
    const recents = (history || []).slice(-5).reverse();
    const pool = [...recents, ...QUICK_PROMPTS];
    const q = value.trim().toLowerCase();
    const filtered = q ? pool.filter((s) => s.toLowerCase().includes(q)) : pool;
    // De-dupe while preserving order.
    return [...new Set(filtered)].slice(0, 5);
  }, [value, history]);

  return (
    <div className="p-2.5 bg-white border-t border-gray-200 shrink-0 relative">
      {showSuggest && suggestions.length > 0 && (
        <div className="absolute bottom-full left-2.5 right-2.5 mb-1 bg-white border border-gray-200 rounded-lg shadow-md max-h-36 overflow-y-auto z-10">
          {suggestions.map((s, i) => (
            <button key={i} type="button" onMouseDown={() => { onChange(s); setShowSuggest(false); }}
              className="w-full text-left px-3 py-1.5 text-[12px] text-gray-700 hover:bg-blue-50 border-b border-gray-50 last:border-0">
              {s}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <button onClick={onAttach} disabled={busy} title="Attach a document or spreadsheet" className="p-2.5 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100 disabled:opacity-40"><Paperclip size={17} /></button>
        <textarea rows={1} value={value} onChange={(e) => onChange(e.target.value)}
          onFocus={() => setShowSuggest(true)} onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); setShowSuggest(false); onSend(); } }}
          placeholder={placeholder} className="flex-1 resize-none max-h-28 px-3 py-2.5 text-[13px] border border-gray-200 rounded-xl outline-none focus:border-[#0020C2]" />
        <button onClick={onSend} disabled={busy || !value.trim()} className="p-2.5 rounded-xl text-white disabled:opacity-40" style={{ background: BRAND }}><PaperPlaneRight size={17} weight="fill" /></button>
      </div>
    </div>
  );
}

/* ── wizard panel (used in both pre-chat and chat mode) ──────────────────── */
function WizardPanel({ values, onChange, fieldErrors, fieldWarnings, result, validating, busy, onValidate, onCreate, compact, user }) {
  const ok = result && ["ok", "success"].includes((result.status || "").toLowerCase());
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {!compact && (
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> Guided wizard
            <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">· direct create, no AI</span>
          </div>
        )}
        {/* Read-only Preparer line, sourced from the logged-in user — shown in
            BOTH the pre-chat (compact) and chat-mode renders of this shared
            panel. The free-text preparer_email field below (from WizardFields)
            stays visible until the backend marks it hidden — harmless
            temporary duplication, expected until that lands. */}
        <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
          <UserIcon size={11} weight="bold" className="text-gray-400 shrink-0" />
          <span className="text-gray-400">Preparer:</span>
          <span className="font-medium text-gray-700">{user?.email || "—"} (you)</span>
        </div>
        <WizardFields agentKey={AGENT_KEY} values={values} onChange={onChange}
          missingFields={Object.keys(fieldErrors)} fieldErrors={fieldErrors} fieldWarnings={fieldWarnings} />
        {result && (
          <div className={`border px-3 py-2 text-xs flex items-start gap-2 rounded ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-[#DC2626] bg-red-50 text-[#DC2626]"}`}>
            {ok ? <CheckCircle size={13} weight="fill" className="mt-0.5 shrink-0" /> : <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" />}
            <span className="font-bold">{typeof result.message === "string" ? result.message : JSON.stringify(result.message)}{result.requisition_number && <span className="font-mono ml-1 opacity-70">#{result.requisition_number}</span>}</span>
          </div>
        )}
      </div>
      <div className="shrink-0 border-t border-gray-200 p-3 bg-gray-50 flex gap-2">
        <button onClick={onValidate} disabled={validating || busy}
          className="flex-1 border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider disabled:opacity-50 rounded flex items-center justify-center gap-1.5">
          <Sparkle size={12} weight="fill" />{validating ? "Validating…" : "Dry-run validate"}
        </button>
        <button onClick={onCreate} disabled={busy || validating}
          className="flex-1 bg-[#0020C2] text-white px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50 rounded flex items-center justify-center gap-1.5">
          {busy ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <PaperPlaneRight size={12} weight="fill" />}
          {busy ? "Creating…" : "Send to Oracle Fusion"}
        </button>
      </div>
    </div>
  );
}

/* ── bulk submit-for-approval: confirm card + results ────────────────────── */
function BulkSubmitCard({ picked, busy, onApprove, onCancel }) {
  const rows = picked || [];
  return (
    <div className="border-2 rounded-xl bg-white shadow-md p-3.5" style={{ borderColor: BRAND }}>
      <div className="flex items-center gap-2 mb-1"><PaperPlaneRight size={16} weight="fill" style={{ color: BRAND }} /><span className="text-[13px] font-semibold text-gray-900">Submit {rows.length} requisition(s) for approval?</span></div>
      <p className="text-[11px] text-gray-500 mb-2">The most recent drafts (newest first). Each is funds-checked before it is submitted.</p>
      <div className="max-h-52 overflow-auto border border-gray-100 rounded-lg mb-3">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between px-2.5 py-1.5 text-[12px] border-b border-gray-50 last:border-0">
            <span className="font-medium text-gray-900">{r.requisition_number || r.requisition_header_id}</span>
            <span className="text-[10px] text-gray-400">{(r.creation_date || "").slice(0, 10)} · {r.document_status || "Draft"}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button disabled={busy} onClick={onApprove} className="flex-1 text-[13px] font-medium text-white py-2.5 rounded-lg disabled:opacity-50" style={{ background: BRAND }}>{busy ? "Submitting…" : `Submit ${rows.length} for approval`}</button>
        <button disabled={busy} onClick={onCancel} className="flex-1 text-[13px] font-medium text-gray-700 py-2.5 rounded-lg border border-gray-300 disabled:opacity-50">Cancel</button>
      </div>
    </div>
  );
}
function BulkSubmitResultCard({ results, created, total }) {
  return (
    <div className="border-2 rounded-xl bg-white shadow-sm p-3" style={{ borderColor: created === total ? "#059669" : `${BRAND}30` }}>
      <div className="flex items-center gap-2 mb-2"><CheckCircle size={15} weight="fill" style={{ color: created === total ? "#059669" : BRAND }} /><span className="text-[13px] font-semibold text-gray-900">Submitted {created} of {total} for approval</span></div>
      <div className="space-y-1.5 max-h-72 overflow-auto">
        {(results || []).map((r, i) => (
          <div key={i} className={`flex items-start gap-2 text-[12px] px-2.5 py-1.5 rounded-lg border ${r.success ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
            {r.success ? <CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> : <WarningCircle size={13} weight="fill" className="text-red-500 mt-0.5 shrink-0" />}
            <div className="min-w-0">
              <div className="font-medium text-gray-900">{r.requisition_number}</div>
              <div className="text-gray-500 break-words">{r.success ? `Submitted — ${r.status || "Pending approval"}` : (r.error || "Failed")}</div>
              {!r.success && r.likely_cause && <div className="text-[10px] text-gray-400 break-words mt-0.5">{r.likely_cause}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── add-line / update confirm cards (editing an EXISTING requisition via chat) ── */
function AddLineConfirmCard({ pending, busy, onConfirm, onCancel }) {
  const ln = pending?.line || {};
  const proj = ln.distributions?.find((d) => d.project_id || d.project_number);
  return (
    <div className="border-2 rounded-xl bg-white shadow-md p-3.5" style={{ borderColor: BRAND }}>
      <div className="flex items-center gap-2 mb-2"><PencilSimple size={16} weight="fill" style={{ color: BRAND }} />
        <span className="text-[13px] font-semibold text-gray-900">Add this line to requisition {pending?.requisition_ref}?</span></div>
      <div className="border border-gray-100 rounded-lg px-2.5 py-2 text-[12px] mb-3">
        <div className="font-medium text-gray-900">{ln.item_description || (ln.item_id ? `Item ${ln.item_id}` : "New line")}</div>
        <div className="text-gray-500">{ln.quantity_based === false ? `${ln.amount} ${ln.currency_code} (fixed price)` : `${ln.quantity || "—"} ${ln.uom_code || ""} × ${ln.price || "—"} ${ln.currency_code || ""}`}</div>
        <div className="text-gray-400">{ln.destination_type_code} · to {ln.deliver_to_location_code || ln.deliver_to_location_id || "—"} · by {ln.requested_delivery_date || "—"}</div>
        {proj && <div className="text-gray-400">Project: {proj.project_number || proj.project_id}{proj.task_number ? ` · Task ${proj.task_number}` : ""}</div>}
      </div>
      <div className="flex gap-2">
        <button disabled={busy} onClick={onConfirm} className="flex-1 text-[13px] font-medium text-white py-2.5 rounded-lg disabled:opacity-50" style={{ background: BRAND }}>{busy ? "Adding…" : "Add line"}</button>
        <button disabled={busy} onClick={onCancel} className="flex-1 text-[13px] font-medium text-gray-700 py-2.5 rounded-lg border border-gray-300 disabled:opacity-50">Cancel</button>
      </div>
      <p className="text-[10px] text-gray-400 mt-2 text-center">Nothing changes in Oracle until you confirm.</p>
    </div>
  );
}
function UpdateConfirmCard({ pending, busy, onConfirm, onCancel }) {
  const header = pending?.header_fields;
  const lineEdits = pending?.line_edits || [];
  return (
    <div className="border-2 rounded-xl bg-white shadow-md p-3.5" style={{ borderColor: BRAND }}>
      <div className="flex items-center gap-2 mb-2"><PencilSimple size={16} weight="fill" style={{ color: BRAND }} />
        <span className="text-[13px] font-semibold text-gray-900">Update requisition {pending?.requisition_ref}?</span></div>
      <div className="border border-gray-100 rounded-lg px-2.5 py-2 text-[12px] mb-3 space-y-1">
        {header && Object.entries(header).map(([k, v]) => <div key={k}><span className="text-gray-400">{k.replace(/_/g, " ")}</span> → <span className="text-gray-900">{String(v)}</span></div>)}
        {lineEdits.map((e, i) => (
          <div key={i}>
            <div className="text-gray-400">Line {e.line_id}:</div>
            {Object.entries(e).filter(([k]) => k !== "line_id").map(([k, v]) => <div key={k} className="pl-2">{k.replace(/_/g, " ")} → <span className="text-gray-900">{String(v)}</span></div>)}
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button disabled={busy} onClick={onConfirm} className="flex-1 text-[13px] font-medium text-white py-2.5 rounded-lg disabled:opacity-50" style={{ background: BRAND }}>{busy ? "Updating…" : "Apply update"}</button>
        <button disabled={busy} onClick={onCancel} className="flex-1 text-[13px] font-medium text-gray-700 py-2.5 rounded-lg border border-gray-300 disabled:opacity-50">Cancel</button>
      </div>
      <p className="text-[10px] text-gray-400 mt-2 text-center">Nothing changes in Oracle until you confirm.</p>
    </div>
  );
}

/* ── post-create card: charge account + funds + submit-for-approval (2-step) ── */
function PostCreateCard({ info, busy, onApplyAccount, onSubmit }) {
  const [manual, setManual] = React.useState("");
  const needsAccount = info.charge_account_status === "missing" || info.needs_charge_account;
  const fundsFailed = info.funds_passed === false || info.insufficient_funds;
  const canSubmit = info.can_submit && !info.submitted;
  const chargeLabel = {
    derived: "Derived automatically from the account rules",
    user_supplied: "Set from the account you entered",
    missing: "Needs a valid charge account",
  }[info.charge_account_status] || "—";
  return (
    <div className="border-2 rounded-xl bg-white shadow-md p-3.5" style={{ borderColor: BRAND }}>
      <div className="flex items-center gap-2 mb-2.5">
        <Receipt size={17} weight="fill" style={{ color: BRAND }} />
        <span className="text-[13px] font-semibold text-gray-900">
          Draft {info.requisition_number} created — review before submitting
        </span>
      </div>

      {/* charge account */}
      <div className="flex items-start gap-2 text-[12px] mb-2">
        {needsAccount ? <WarningCircle size={14} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
          : <CheckCircle size={14} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />}
        <div>
          <div className="font-medium text-gray-800">Charge account</div>
          <div className="text-gray-500">{chargeLabel}</div>
        </div>
      </div>
      {needsAccount && (
        <div className="mb-2.5 pl-6">
          <div className="text-[10px] text-gray-400 mb-1">Oracle couldn't automatically derive an account for one or more lines — pick a valid charge account:</div>
          <LookupSelect lookup="charge_accounts" onPick={(r) => onApplyAccount(String(r.value))} />
          <div className="flex items-center gap-2 mt-1.5">
            <input value={manual} onChange={(e) => setManual(e.target.value)}
              placeholder="…or type a code-combination id / segmented account"
              className="flex-1 text-[11px] border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#0020C2]" />
            <button disabled={busy || !manual.trim()} onClick={() => onApplyAccount(manual.trim())}
              className="text-[11px] text-white px-3 py-1.5 rounded-lg disabled:opacity-50" style={{ background: BRAND }}>Apply</button>
          </div>
        </div>
      )}

      {/* funds */}
      <div className="flex items-start gap-2 text-[12px] mb-3">
        {fundsFailed ? <WarningCircle size={14} weight="fill" className="text-[#DC2626] mt-0.5 shrink-0" />
          : info.funds_status ? <CheckCircle size={14} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
          : <CurrencyDollar size={14} weight="fill" className="text-gray-300 mt-0.5 shrink-0" />}
        <div>
          <div className="font-medium text-gray-800">Funds check {info.validation_mode ? <span className="text-[10px] text-gray-400 font-normal">· {info.validation_mode} mode</span> : null}</div>
          <div className="text-gray-500">
            {info.funds_status
              ? `${info.funds_status}${fundsFailed ? (info.validation_mode === "free" ? " — allowed in Free mode" : " — resolve before submitting, or switch validation to Free") : ""}`
              : (needsAccount ? "Runs once a charge account is in place" : "—")}
          </div>
        </div>
      </div>

      <button disabled={busy || !canSubmit} onClick={onSubmit}
        className="w-full text-[13px] font-medium text-white py-2.5 rounded-lg disabled:opacity-50" style={{ background: BRAND }}>
        {info.submitted ? "Submitted for approval"
          : busy ? "Submitting…"
          : canSubmit ? "Submit for approval"
          : needsAccount ? "Enter a charge account to continue"
          : fundsFailed ? "Funds must pass before submitting" : "Submit for approval"}
      </button>
      <p className="text-[10px] text-gray-400 mt-2 text-center">The requisition is a draft until you submit it for approval.</p>
    </div>
  );
}

/* ── PR dashboard (live from Oracle) ─────────────────────────────────────── */
const STATUS_TABS = [
  { key: "all",      label: "All" },
  { key: "draft",    label: "Draft",    match: (s) => /incomplete|draft|rejected by/i.test(s) },
  { key: "pending",  label: "Pending",  match: (s) => /pending|in process|pre-?approved|submitted/i.test(s) },
  { key: "approved", label: "Approved", match: (s) => /^approved$/i.test(s) },
  { key: "rejected", label: "Rejected", match: (s) => /rejected|returned|withdrawn|canceled|cancelled/i.test(s) },
];
function statusTone(s) {
  if (/^approved$/i.test(s)) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (/reject|withdraw|cancel|return/i.test(s)) return "bg-red-50 text-red-700 border-red-200";
  if (/pending|process|submit/i.test(s)) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-gray-50 text-gray-600 border-gray-200";
}
function DashStat({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex-1 border border-gray-200 rounded-xl p-3 bg-white">
      <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mb-1"><Icon size={13} className={accent} />{label}</div>
      <div className="text-2xl font-black text-gray-900">{value}</div>
    </div>
  );
}

function RecordDetail({ rec, headerId, onClose, onChanged }) {
  const h = rec.record || {};
  const lines = rec.lines || [];
  const id = headerId || h.RequisitionHeaderId;
  const status = h.DocumentStatus || "";
  const isDraft = /incomplete|draft/i.test(status);
  const inFlight = /pending|process|submit|pre-?approved/i.test(status);

  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);            // {tone,text}
  const [approvers, setApprovers] = React.useState(null); // null=not loaded
  const [attachments, setAttachments] = React.useState(null);
  const [distributions, setDistributions] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [editDesc, setEditDesc] = React.useState(h.Description || "");
  const [editJust, setEditJust] = React.useState(h.Justification || "");
  const [lineEditor, setLineEditor] = React.useState(null); // { mode: "add"|"edit", source?, value }
  const [confirm, setConfirm] = React.useState(null);     // {type, reason}

  const run = async (promise, okText) => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await promise;
      const ok = data.status === "ok";
      setMsg({ tone: ok ? "success" : "error", text: data.message || (ok ? okText : "Action failed.") });
      if (ok) onChanged?.();
    } catch (e) { setMsg({ tone: "error", text: e?.response?.data?.detail || e.message }); }
    finally { setBusy(false); setConfirm(null); }
  };
  const [showPdf, setShowPdf] = React.useState(false);   // embed the PDF in-place, not a new tab
  const loadApprovers = async () => {
    setBusy(true);
    try { const { data } = await api.get(`${BASE}/approvers/${id}`); setApprovers(data.approvers || []); if (data.note) setMsg({ tone: "success", text: data.note }); }
    catch { setApprovers([]); } finally { setBusy(false); }
  };
  const loadAttachments = async () => {
    setBusy(true);
    try { const { data } = await api.get(`${BASE}/attachments/${id}`); setAttachments(data.attachments || []); }
    catch { setAttachments([]); } finally { setBusy(false); }
  };
  const loadDistributions = async () => {
    setBusy(true);
    try { const { data } = await api.get(`${BASE}/distributions/${id}`); setDistributions(data.distributions || []); }
    catch { setDistributions([]); } finally { setBusy(false); }
  };
  const [pickingLine, setPickingLine] = React.useState(null);   // LineNumber currently showing the picker
  const applyChargeAccountTo = async (account) => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post(`${BASE}/charge-account/apply`, { requisition_id: id, charge_account: String(account) });
      setMsg({ tone: data.success ? "success" : "error", text: data.message || (data.success ? "Charge account applied." : "Couldn't apply the charge account.") });
      setPickingLine(null);
      if (data.success) await loadDistributions();
    } catch (e) { setMsg({ tone: "error", text: e?.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const saveEdit = () => run(
    api.post(`${BASE}/update`, { session_id: "dash", requisition_id: id,
      header_fields: { description: editDesc, justification: editJust } }),
    "Requisition updated.").then(() => setEditing(false));

  const lineValue = (source = {}) => ({
    item_description: source.ItemDescription || "",
    quantity: source.Quantity ?? 1,
    price: source.Price ?? "",
    uom_code: source.UOMCode || "EA",
    currency_code: source.CurrencyCode || "AED",
    requested_delivery_date: String(source.RequestedDeliveryDate || new Date().toISOString().slice(0, 10)).slice(0, 10),
    note_to_supplier: source.NoteToSupplier || "",
    project_id: "", task_id: "", expenditure_type_id: "", expenditure_item_date: "",
  });
  const openLineEditor = (mode, source = null) => setLineEditor({ mode, source, value: lineValue(source || lines[0] || {}) });
  const changeLine = (key, value) => setLineEditor((e) => ({ ...e, value: { ...e.value, [key]: value } }));
  const saveLine = () => {
    const editor = lineEditor;
    const v = editor.value;
    const numeric = (value) => value === "" ? undefined : Number(value);
    if (editor.mode === "edit") {
      const edit = {
        line_id: editor.source.RequisitionLineId,
        item_description: v.item_description, quantity: numeric(v.quantity), price: numeric(v.price),
        uom_code: v.uom_code, currency_code: v.currency_code,
        requested_delivery_date: v.requested_delivery_date, note_to_supplier: v.note_to_supplier || undefined,
      };
      run(api.post(`${BASE}/update`, { requisition_id: id, line_edits: [edit] }), "Line updated.").then(() => setLineEditor(null));
      return;
    }
    const template = lines[0] || {};
    if (!template.DeliverToLocationId && !template.DeliverToLocationCode) {
      setMsg({ tone: "error", text: "This requisition has no reusable delivery defaults. Add the line through chat so the agent can collect and validate them." });
      return;
    }
    const line = {
      line_type_code: template.LineTypeCode || "Goods", quantity_based: true,
      item_description: v.item_description, category_id: template.CategoryId,
      category_name: template.CategoryName, quantity: numeric(v.quantity), price: numeric(v.price),
      uom_code: v.uom_code, currency_code: v.currency_code, requester_id: template.RequesterId,
      destination_type_code: template.DestinationTypeCode || "EXPENSE",
      destination_organization_id: template.DestinationOrganizationId,
      destination_organization_code: template.DestinationOrganizationCode,
      deliver_to_location_id: template.DeliverToLocationId,
      deliver_to_location_code: template.DeliverToLocationCode,
      requested_delivery_date: v.requested_delivery_date, note_to_supplier: v.note_to_supplier || undefined,
    };
    if (v.project_id || v.task_id || v.expenditure_type_id) {
      line.distributions = [{ quantity: numeric(v.quantity), project_id: numeric(v.project_id), task_id: numeric(v.task_id),
        expenditure_type_id: numeric(v.expenditure_type_id), expenditure_item_date: v.expenditure_item_date || v.requested_delivery_date }];
    }
    run(api.post(`${BASE}/lines`, { requisition_id: id, line }), "Line added.").then(() => setLineEditor(null));
  };

  const ActionBtn = ({ icon: Icon, label, onClick, danger }) => (
    <button disabled={busy} onClick={onClick}
      className={`flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border disabled:opacity-50 ${danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
      <Icon size={13} /> {label}
    </button>
  );

  return (
    <div className="absolute inset-0 bg-white z-20 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
        <button onClick={onClose} className="flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-gray-800"><ArrowLeft size={15} /> Back to list</button>
        {rec.requisition_url && <button onClick={() => window.open(rec.requisition_url, "_blank")} className="flex items-center gap-1 text-[11px] text-[#0020C2] hover:underline">Open in Oracle (REST) <ArrowSquareOut size={12} /></button>}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <div>
          <div className="text-xl font-black text-gray-900">{h.Requisition || h.RequisitionHeaderId}</div>
          <span className={`inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full border ${statusTone(h.DocumentStatus || "")}`}>{h.DocumentStatus || "—"}</span>
        </div>

        {/* actions bar — all buttons; gated by document status */}
        <div className="flex flex-wrap gap-2">
          <ActionBtn icon={FilePdf} label={showPdf ? "Hide PDF" : "View PDF"} onClick={() => setShowPdf((v) => !v)} />
          <ActionBtn icon={UsersThree} label="Approvers" onClick={loadApprovers} />
          <ActionBtn icon={PaperclipIcon} label="Attachments" onClick={loadAttachments} />
          <ActionBtn icon={CurrencyDollar} label="Distributions" onClick={loadDistributions} />
          {isDraft && <ActionBtn icon={PencilSimple} label="Edit" onClick={() => setEditing((v) => !v)} />}
          {isDraft && <ActionBtn icon={Plus} label="Add line" onClick={() => openLineEditor("add")} />}
          {inFlight && <ActionBtn icon={ArrowUUpLeft} label="Withdraw" onClick={() => setConfirm({ type: "withdraw" })} />}
          {(inFlight || isDraft) && <ActionBtn icon={Prohibit} label="Cancel" danger onClick={() => setConfirm({ type: "cancel", reason: "" })} />}
          {isDraft && <ActionBtn icon={Trash} label="Delete" danger onClick={() => setConfirm({ type: "delete" })} />}
        </div>

        {msg && (
          <div className={`text-[12px] px-3 py-2 rounded-lg border flex items-start gap-2 ${msg.tone === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-700"}`}>
            {msg.tone === "success" ? <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0" /> : <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />}
            <span>{msg.text}</span>
          </div>
        )}

        {showPdf && <PdfViewer path={`${BASE}/pdf/${id}`} filename={`requisition_${h.Requisition || id}.pdf`} height={480} />}

        {/* confirmation prompt for destructive/state-changing actions */}
        {confirm && (
          <div className="border-2 rounded-xl p-3 bg-white shadow-sm" style={{ borderColor: confirm.type === "delete" || confirm.type === "cancel" ? "#DC2626" : BRAND }}>
            <div className="text-[12px] font-semibold text-gray-900 mb-1.5">
              {confirm.type === "delete" ? "Delete this draft requisition?" : confirm.type === "withdraw" ? "Withdraw from approval?" : "Cancel this requisition?"}
            </div>
            {confirm.type === "cancel" && (
              <textarea value={confirm.reason} onChange={(e) => setConfirm({ ...confirm, reason: e.target.value })}
                rows={2} placeholder="Reason (optional)" className="w-full text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 mb-2 outline-none focus:border-[#0020C2]" />
            )}
            <div className="flex gap-2">
              <button disabled={busy} onClick={() => {
                if (confirm.type === "delete") run(api.post(`${BASE}/delete`, { requisition_id: id }), "Requisition deleted.");
                else if (confirm.type === "withdraw") run(api.post(`${BASE}/withdraw`, { requisition_id: id }), "Withdrawn.");
                else run(api.post(`${BASE}/cancel`, { requisition_id: id, reason: confirm.reason || null }), "Cancelled.");
              }} className="text-[12px] font-medium text-white px-3 py-1.5 rounded-lg disabled:opacity-50" style={{ background: confirm.type === "withdraw" ? BRAND : "#DC2626" }}>
                {busy ? "Working…" : "Confirm"}
              </button>
              <button disabled={busy} onClick={() => setConfirm(null)} className="text-[12px] text-gray-600 px-3 py-1.5 rounded-lg border border-gray-200">Keep it</button>
            </div>
          </div>
        )}

        {/* inline edit (drafts only) */}
        {editing && (
          <div className="border rounded-xl p-3 bg-gray-50" style={{ borderColor: `${BRAND}30` }}>
            <div className="text-[11px] font-semibold text-gray-600 mb-1">Description</div>
            <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={2} className="w-full text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 mb-2 outline-none focus:border-[#0020C2]" />
            <div className="text-[11px] font-semibold text-gray-600 mb-1">Justification</div>
            <textarea value={editJust} onChange={(e) => setEditJust(e.target.value)} rows={2} className="w-full text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 mb-2 outline-none focus:border-[#0020C2]" />
            <button disabled={busy} onClick={saveEdit} className="text-[12px] font-medium text-white px-3 py-1.5 rounded-lg disabled:opacity-50" style={{ background: BRAND }}>{busy ? "Saving…" : "Save changes"}</button>
          </div>
        )}

        {lineEditor && (
          <div className="border rounded-xl p-3 bg-gray-50 space-y-2" style={{ borderColor: `${BRAND}30` }}>
            <div className="text-[12px] font-semibold text-gray-800">{lineEditor.mode === "add" ? "Add a line" : `Edit line ${lineEditor.source.LineNumber}`}</div>
            {lineEditor.mode === "add" && <div className="text-[11px] text-gray-500">Delivery, requester, category, and destination are copied from line 1. Use chat when this line needs different Oracle-validated values.</div>}
            <textarea value={lineEditor.value.item_description} onChange={(e) => changeLine("item_description", e.target.value)} rows={2} placeholder="Item description" className="w-full text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#0020C2]" />
            <div className="grid grid-cols-2 gap-2">
              <input value={lineEditor.value.quantity} onChange={(e) => changeLine("quantity", e.target.value)} type="number" min="0" step="any" placeholder="Quantity" className="text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#0020C2]" />
              <input value={lineEditor.value.price} onChange={(e) => changeLine("price", e.target.value)} type="number" min="0" step="any" placeholder="Unit price" className="text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#0020C2]" />
              <input value={lineEditor.value.uom_code} onChange={(e) => changeLine("uom_code", e.target.value)} placeholder="UOM" className="text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#0020C2]" />
              <input value={lineEditor.value.currency_code} onChange={(e) => changeLine("currency_code", e.target.value)} placeholder="Currency" className="text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#0020C2]" />
            </div>
            <input value={lineEditor.value.requested_delivery_date} onChange={(e) => changeLine("requested_delivery_date", e.target.value)} type="date" className="w-full text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#0020C2]" />
            <textarea value={lineEditor.value.note_to_supplier} onChange={(e) => changeLine("note_to_supplier", e.target.value)} rows={2} placeholder="Note to supplier (optional)" className="w-full text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#0020C2]" />
            {lineEditor.mode === "add" && <details className="text-[11px] text-gray-600"><summary className="cursor-pointer">Project-funded line (optional)</summary><div className="grid grid-cols-2 gap-2 mt-2"><input value={lineEditor.value.project_id} onChange={(e) => changeLine("project_id", e.target.value)} placeholder="Project ID" className="border border-gray-200 rounded-lg px-2 py-1.5" /><input value={lineEditor.value.task_id} onChange={(e) => changeLine("task_id", e.target.value)} placeholder="Task ID" className="border border-gray-200 rounded-lg px-2 py-1.5" /><input value={lineEditor.value.expenditure_type_id} onChange={(e) => changeLine("expenditure_type_id", e.target.value)} placeholder="Expenditure type ID" className="border border-gray-200 rounded-lg px-2 py-1.5" /><input value={lineEditor.value.expenditure_item_date} onChange={(e) => changeLine("expenditure_item_date", e.target.value)} type="date" className="border border-gray-200 rounded-lg px-2 py-1.5" /></div><div className="mt-1 text-gray-400">Choose project, task, and expenditure type in chat to look up their IDs; project account rules will derive the charge account.</div></details>}
            <div className="flex gap-2"><button disabled={busy} onClick={saveLine} className="text-[12px] font-medium text-white px-3 py-1.5 rounded-lg disabled:opacity-50" style={{ background: BRAND }}>{busy ? "Saving…" : lineEditor.mode === "add" ? "Add line" : "Save line"}</button><button disabled={busy} onClick={() => setLineEditor(null)} className="text-[12px] text-gray-600 px-3 py-1.5 rounded-lg border border-gray-200">Cancel</button></div>
          </div>
        )}

        {/* approvers */}
        {approvers && (
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">Current approvers</div>
            {approvers.length === 0 ? <div className="px-3 py-2 text-[12px] text-gray-400">No current approvers returned.</div>
              : approvers.map((a, i) => (
                <div key={i} className="px-3 py-2 text-[12px] border-b border-gray-50 last:border-0 flex items-center justify-between">
                  <span className="text-gray-900">{a.name || a.raw?.ApproverName || "Approver"}</span>
                  <span className="text-[10px] text-gray-400">{[a.status, a.stage].filter(Boolean).join(" · ") || ""}</span>
                </div>
              ))}
          </div>
        )}

        {/* attachments */}
        {attachments && (
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">Attachments</div>
            {attachments.length === 0 ? <div className="px-3 py-2 text-[12px] text-gray-400">No attachments on this requisition.</div>
              : attachments.map((a, i) => (
                <div key={i} className="px-3 py-2 text-[12px] border-b border-gray-50 last:border-0 flex items-center gap-2">
                  <FilePdf size={14} className="text-gray-400 shrink-0" />
                  <div className="min-w-0 flex-1"><div className="text-gray-900 truncate">{a.FileName || a.Title || "Attachment"}</div>{a.Description && <div className="text-[10px] text-gray-400 truncate">{a.Description}</div>}</div>
                  <button disabled={busy} onClick={() => downloadViaApi(`${BASE}/attachment/${id}/${a.AttachedDocumentId}`, a.FileName || "attachment")}
                    className="text-[11px] text-[#0020C2] hover:underline flex items-center gap-1 shrink-0"><DownloadSimple size={13} /> Download</button>
                </div>
              ))}
          </div>
        )}

        {distributions && (
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">Distributions</div>
            {distributions.length === 0 ? <div className="px-3 py-2 text-[12px] text-gray-400">No distributions on this requisition.</div>
              : distributions.map((d, i) => {
                  const hasAccount = d.ChargeAccountId || d.ChargeAccount;
                  return (
                    <div key={i} className="px-3 py-2 text-[12px] border-b border-gray-50 last:border-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">Line {d.LineNumber} · Dist {d.DistributionNumber}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${hasAccount ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                          {hasAccount ? "Charge account set" : "Missing charge account"}
                        </span>
                      </div>
                      {d.ItemDescription && <div className="text-gray-500 truncate">{d.ItemDescription}</div>}
                      <div className="text-gray-600 mt-0.5">
                        {d.Quantity != null ? `Qty ${d.Quantity} · ` : ""}{d.CurrencyAmount != null ? `${d.CurrencyAmount}` : "—"}
                        {d.BudgetDate ? ` · budget ${String(d.BudgetDate).slice(0, 10)}` : ""}
                      </div>
                      {hasAccount && <div className="text-[10px] font-mono text-gray-400 mt-0.5 break-all">{d.ChargeAccount || d.ChargeAccountId}</div>}
                      {d.FundsStatus && <div className="text-[10px] text-gray-400 mt-0.5">Funds: {d.FundsStatus}</div>}
                      {!hasAccount && (
                        pickingLine === d.LineNumber ? (
                          <div className="mt-1.5">
                            <LookupSelect lookup="charge_accounts" onPick={(r) => applyChargeAccountTo(r.value)} />
                            <button onClick={() => setPickingLine(null)} className="text-[10px] text-gray-400 hover:text-gray-600 mt-1">Cancel</button>
                          </div>
                        ) : (
                          <button disabled={busy} onClick={() => setPickingLine(d.LineNumber)}
                            className="mt-1.5 text-[11px] text-[#0020C2] hover:underline disabled:opacity-50">Pick a charge account…</button>
                        )
                      )}
                    </div>
                  );
                })}
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
          <div><div className="text-gray-400">Description</div><div className="text-gray-900">{h.Description || "—"}</div></div>
          <div><div className="text-gray-400">Business unit</div><div className="text-gray-900">{h.RequisitioningBU || "—"}</div></div>
          <div><div className="text-gray-400">Preparer</div><div className="text-gray-900">{h.Preparer || "—"}</div></div>
          <div><div className="text-gray-400">Funds status</div><div className="text-gray-900">{h.FundsStatus || "—"}</div></div>
          <div><div className="text-gray-400">Created</div><div className="text-gray-900">{(h.CreationDate || "").slice(0, 10) || "—"}</div></div>
          <div><div className="text-gray-400">Approved</div><div className="text-gray-900">{(h.ApprovedDate || "").slice(0, 10) || "—"}</div></div>
        </div>
        <div>
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Lines ({lines.length})</div>
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            {lines.length === 0 && <div className="px-3 py-2 text-[12px] text-gray-400">No line detail returned.</div>}
            {lines.map((ln, i) => (
              <div key={i} className="px-3 py-2 text-[12px] border-b border-gray-50 last:border-0">
                <div className="flex items-start justify-between gap-2"><div className="font-medium text-gray-900">{ln.LineNumber}. {ln.ItemDescription || ln.Item || "Line"}</div>{isDraft && <button disabled={busy} onClick={() => openLineEditor("edit", ln)} className="text-[11px] text-[#0020C2] hover:underline shrink-0">Edit</button>}</div>
                <div className="text-gray-500">
                  {ln.Quantity != null ? `${ln.Quantity} ${ln.UOMCode || ""} × ${ln.Price ?? "—"} ${ln.CurrencyCode || ""}` : (ln.LineTypeCode || "")}
                  {ln.DeliverToLocationCode ? ` · to ${ln.DeliverToLocationCode}` : ""}{ln.RequestedDeliveryDate ? ` · by ${String(ln.RequestedDeliveryDate).slice(0, 10)}` : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PrDashboard() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [tab, setTab] = React.useState("all");
  const [detail, setDetail] = React.useState(null);
  const [detailId, setDetailId] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true); setErr("");
    try {
      // Feature A: a non-empty search routes to /search (structured filters). A term
      // that looks like a requisition number is matched exactly; otherwise it's a
      // description prefix search. Empty search → the full recent list.
      const q = search.trim();
      const isNumber = /-|^\d{3,}$/.test(q);
      const path = q ? `${BASE}/search` : `${BASE}/list`;
      const params = q ? (isNumber ? { number: q, limit: 200 } : { q, limit: 200 }) : { limit: 200 };
      const { data } = await api.get(path, { params });
      if (data.status === "unconfigured") { setErr("Connect your Oracle profile to view requisitions."); setRows([]); }
      else if (data.status !== "ok") { setErr(data.message || "Couldn't load requisitions."); setRows([]); }
      else setRows(data.items || []);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); setRows([]); }
    finally { setLoading(false); }
  }, [search]);
  React.useEffect(() => { const t = setTimeout(load, search ? 320 : 0); return () => clearTimeout(t); }, [load, search]);

  const openRecord = async (id) => {
    setDetailLoading(true); setDetail({ loading: true }); setDetailId(id);
    try {
      const { data } = await api.get(`${BASE}/record/${id}`);
      if (data.status === "ok") setDetail(data); else { setDetail(null); }
    } catch { setDetail(null); }
    finally { setDetailLoading(false); }
  };
  const closeDetail = () => { setDetail(null); setDetailId(null); };
  const afterChange = () => { closeDetail(); load(); };

  const counts = React.useMemo(() => {
    const c = { total: rows.length, approved: 0, pending: 0, draft: 0 };
    rows.forEach((r) => {
      const s = r.DocumentStatus || "";
      if (/^approved$/i.test(s)) c.approved++;
      else if (/pending|process|submit/i.test(s)) c.pending++;
      else if (/incomplete|draft/i.test(s)) c.draft++;
    });
    return c;
  }, [rows]);

  const filtered = React.useMemo(() => {
    const t = STATUS_TABS.find((x) => x.key === tab);
    if (!t || !t.match) return rows;
    return rows.filter((r) => t.match(r.DocumentStatus || ""));
  }, [rows, tab]);

  return (
    <div className="relative flex-1 min-h-0 flex flex-col bg-gray-50">
      {detail && !detail.loading && <RecordDetail rec={detail} headerId={detailId} onClose={closeDetail} onChanged={afterChange} />}
      {detailLoading && <div className="absolute inset-0 bg-white/70 z-30 flex items-center justify-center text-[12px] text-gray-500">Loading record…</div>}

      <div className="p-4 space-y-3 overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5"><SquaresFour size={12} weight="fill" /> Requisitions</div>
          <button onClick={load} disabled={loading} className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-[#0020C2] disabled:opacity-50"><ArrowClockwise size={13} className={loading ? "animate-spin" : ""} /> Refresh</button>
        </div>

        {/* Feature A: search by requisition number or description */}
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white">
          <MagnifyingGlass size={14} className="text-gray-400 shrink-0" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by requisition number or description…"
            className="w-full text-[12px] outline-none" />
          {search && <button onClick={() => setSearch("")} className="text-gray-400 hover:text-gray-700 shrink-0"><X size={14} /></button>}
        </div>

        <div className="flex gap-2">
          <DashStat icon={ClipboardText} label="Total" value={counts.total} accent="text-gray-400" />
          <DashStat icon={CheckCircle} label="Approved" value={counts.approved} accent="text-emerald-500" />
          <DashStat icon={Clock} label="Pending" value={counts.pending} accent="text-amber-500" />
          <DashStat icon={Receipt} label="Draft" value={counts.draft} accent="text-[#0020C2]" />
        </div>

        <div className="flex gap-4 border-b border-gray-200">
          {STATUS_TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`pb-2 text-[12px] font-medium border-b-2 -mb-px ${tab === t.key ? "border-[#0020C2] text-[#0020C2]" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {err && <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{err}</div>}
        {loading ? <div className="text-[12px] text-gray-400 py-6 text-center">Loading requisitions from Oracle…</div>
          : filtered.length === 0 && !err ? <div className="text-[12px] text-gray-400 py-6 text-center">No requisitions in this view.</div>
          : (
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              {filtered.map((r) => (
                <button key={r.RequisitionHeaderId} onClick={() => openRecord(r.RequisitionHeaderId)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-gray-50 last:border-0 hover:bg-blue-50/50">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-gray-900 truncate">{r.Requisition || r.RequisitionHeaderId}</div>
                    <div className="text-[11px] text-gray-500 truncate">{r.Description || "—"} · {r.RequisitioningBU || ""}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${statusTone(r.DocumentStatus || "")}`}>{r.DocumentStatus || "—"}</span>
                  <span className="text-[10px] text-gray-400 shrink-0 w-20 text-right">{(r.CreationDate || "").slice(0, 10)}</span>
                  <CaretRight size={13} className="text-gray-300 shrink-0" />
                </button>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

/* ── main modal (centered, matches the other agents) ─────────────────────── */
export default function PurchaseReqAgentModal() {
  const { openAgent, setOpenAgent, setSetupOpen, profile } = useErp();
  const { user } = useAuth();
  const [sessionId] = React.useState(() => crypto.randomUUID());
  const [chatStarted, setChatStarted] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const [phraseIdx, setPhraseIdx] = React.useState(0);
  const [view, setView] = React.useState("create");   // "create" | "dashboard"
  const [validationMode, setValidationMode] = React.useState("strict"); // "strict" | "free"
  // Most recently picked business_unit/project ids, so a dependent field card
  // (project ← business_unit, project_task ← project) can scope its dropdown.
  const [lookupContext, setLookupContext] = React.useState({});
  const onRawPick = async (field, row, itemId) => {
    if (field === "business_unit") setLookupContext((c) => ({ ...c, buId: row.value }));
    if (field === "project") setLookupContext((c) => ({ ...c, projectId: row.value }));
    if (field === "preparer") {
      // Preparer never flows through the LLM — set it directly, bypassing send().
      setItems((xs) => xs.map((x) => (x.id === itemId ? { ...x, answered: true } : x)));
      const loadingId = pushLoading(); setBusy(true);
      try {
        await api.post(`${BASE}/set-preparer`, { session_id: sessionId, preparer_id: row.value });
        resolveLoading(loadingId, `Preparer set to ${row.label}. Please repeat your request.`, "success");
      } catch (e) {
        resolveLoading(loadingId, `Couldn't set the preparer: ${e?.response?.data?.detail || e.message}`, "error");
      } finally { setBusy(false); }
    }
  };

  // guided wizard (manual path)
  const [wizardValues, setWizardValues] = React.useState({});
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [fieldWarnings, setFieldWarnings] = React.useState({});
  const [wizardResult, setWizardResult] = React.useState(null);
  const [validating, setValidating] = React.useState(false);
  const [wizardBusy, setWizardBusy] = React.useState(false);

  const scrollRef = React.useRef(null);
  const fileRef = React.useRef(null);
  const isOpen = openAgent === AGENT_KEY;

  React.useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [items]);
  React.useEffect(() => { if (!busy) return; const t = setInterval(() => setPhraseIdx((i) => i + 1), 1800); return () => clearInterval(t); }, [busy]);

  // Feeds the composer's quick-suggestions with this session's own recent
  // messages, alongside the static starter prompts. Must be declared before the
  // early `isOpen` return below (hooks can't be conditional).
  const userHistory = React.useMemo(
    () => items.filter((x) => x.kind === "chat" && x.role === "user").map((x) => x.text),
    [items]
  );

  if (!isOpen) return null;
  const close = () => setOpenAgent(null);

  const push = (item) => setItems((xs) => [...xs, { id: Date.now() + Math.random(), ...item }]);
  const pushLoading = () => { const id = Date.now() + Math.random(); setItems((xs) => [...xs, { id, kind: "chat", role: "assistant", loading: true }]); return id; };
  const resolveLoading = (id, text, tone) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, loading: false, text, tone } : x)));

  const payloadFV = () => {
    const out = {};
    for (const [k, v] of Object.entries(wizardValues)) out[k] = Array.isArray(v) && v[0]?.attachment_id ? v.map((i) => i.attachment_id) : v;
    return out;
  };

  async function send(text) {
    const userText = (text ?? input).trim();
    if (!userText || busy) return;
    setInput(""); setChatStarted(true);
    // Dismiss any prior interactive cards so a follow-up never re-shows a stale
    // confirmation/field card (e.g. after a rejected create). A fresh one is
    // pushed only if the agent re-stages this turn.
    setItems((xs) => xs.filter((x) => x.kind !== "confirm" && x.kind !== "bulk_submit").map((x) => (x.kind === "field" ? { ...x, answered: true } : x)));
    push({ kind: "chat", role: "user", text: userText });
    const loadingId = pushLoading(); setBusy(true);
    try {
      const { data } = await api.post(`${BASE}/chat`, { session_id: sessionId, message: userText });
      if (data.status === "unconfigured") { resolveLoading(loadingId, data.response); setSetupOpen?.(true); return; }
      resolveLoading(loadingId, data.response || "…");
      setDraft(data.draft || null);
      if (data.awaiting_confirmation && data.draft) {
        // Inline confirm card — see the ChatStream render switch below.
        push({ kind: "confirm", draft: data.draft });
      }
      else if (data.awaiting_bulk_submit && data.bulk_submit) push({ kind: "bulk_submit", picked: data.bulk_submit });
      else if (data.next_field_ui) push({ kind: "field", fieldUi: data.next_field_ui, answered: false });
      // Feature E: a PDF the agent made available this turn (independent of the above).
      if (data.pdf_ready) push({ kind: "pdf", info: data.pdf_ready });
      if (data.add_line_ready) push({ kind: "add_line_confirm", pending: data.add_line_ready });
      if (data.update_ready) push({ kind: "update_confirm", pending: data.update_ready });
    } catch (e) { resolveLoading(loadingId, `Something went wrong: ${e?.response?.data?.detail || e.message}`, "error"); }
    finally { setBusy(false); }
  }
  async function confirmSubmit() {
    setBusy(true); const loadingId = pushLoading();
    try {
      const { data } = await api.post(`${BASE}/confirm`, { session_id: sessionId, validation_mode: validationMode });
      const ok = data.status === "ok";
      resolveLoading(loadingId, data.message || (ok ? "Created." : "Oracle rejected the requisition."), ok ? "success" : "error");
      if (ok) {
        // The PostCreateCard two-step flow (charge account / funds / submit-for-
        // approval) becomes immediately visible right after, below the chat.
        setDraft(null);
        setItems((xs) => xs.filter((x) => x.kind !== "confirm"));
        // Two-step: the draft now exists; show the charge-account / funds / submit card.
        push({ kind: "postcreate", info: {
          requisition_number: data.requisition_number,
          charge_account_status: data.charge_account_status,
          needs_charge_account: data.needs_charge_account,
          funds_status: data.funds_status,
          funds_passed: data.funds_passed,
          insufficient_funds: data.insufficient_funds,
          can_submit: data.can_submit,
          submitted: false,
        } });
      }
      // On rejection the confirm card stays so the user can fix values and resubmit.
    } catch (e) { resolveLoading(loadingId, `Submission failed: ${e?.response?.data?.detail || e.message}`, "error"); }
    finally { setBusy(false); }
  }
  async function applyChargeAccount(account, itemId) {
    setBusy(true); const loadingId = pushLoading();
    try {
      const { data } = await api.post(`${BASE}/charge-account`, { session_id: sessionId, charge_account: account });
      const ok = data.status === "ok";
      resolveLoading(loadingId, data.message || (ok ? "Charge account applied." : "Couldn't apply the charge account."), ok ? "success" : "error");
      setItems((xs) => xs.map((x) => (x.kind === "postcreate" && x.id === itemId)
        ? { ...x, info: { ...x.info, charge_account_status: data.charge_account_status,
            needs_charge_account: data.charge_account_status === "missing",
            funds_status: data.funds_status, funds_passed: data.funds_passed,
            insufficient_funds: data.insufficient_funds, can_submit: data.can_submit } }
        : x));
    } catch (e) { resolveLoading(loadingId, `Couldn't set the charge account: ${e?.response?.data?.detail || e.message}`, "error"); }
    finally { setBusy(false); }
  }
  async function submitForApproval(itemId) {
    setBusy(true); const loadingId = pushLoading();
    try {
      const { data } = await api.post(`${BASE}/submit`, { session_id: sessionId, validation_mode: validationMode });
      const ok = data.status === "ok";
      resolveLoading(loadingId, data.message || (ok ? "Submitted for approval." : "Submit did not complete."), ok ? "success" : "error");
      if (ok) setItems((xs) => xs.map((x) => (x.kind === "postcreate" && x.id === itemId)
        ? { ...x, info: { ...x.info, submitted: true, can_submit: false } } : x));
    } catch (e) { resolveLoading(loadingId, `Submit failed: ${e?.response?.data?.detail || e.message}`, "error"); }
    finally { setBusy(false); }
  }
  async function confirmAddLine(itemId) {
    setItems((xs) => xs.filter((x) => x.id !== itemId));
    setBusy(true); const loadingId = pushLoading();
    try {
      const { data } = await api.post(`${BASE}/confirm-add-line`, { session_id: sessionId });
      resolveLoading(loadingId, data.message || (data.status === "ok" ? "Line added." : "Couldn't add the line."), data.status === "ok" ? "success" : "error");
    } catch (e) { resolveLoading(loadingId, `Couldn't add the line: ${e?.response?.data?.detail || e.message}`, "error"); }
    finally { setBusy(false); }
  }
  async function confirmUpdate(itemId) {
    setItems((xs) => xs.filter((x) => x.id !== itemId));
    setBusy(true); const loadingId = pushLoading();
    try {
      const { data } = await api.post(`${BASE}/confirm-update`, { session_id: sessionId });
      resolveLoading(loadingId, data.message || (data.status === "ok" ? "Requisition updated." : "Couldn't apply the update."), data.status === "ok" ? "success" : "error");
    } catch (e) { resolveLoading(loadingId, `Couldn't apply the update: ${e?.response?.data?.detail || e.message}`, "error"); }
    finally { setBusy(false); }
  }
  async function approveBulkSubmit(itemId) {
    setItems((xs) => xs.filter((x) => x.id !== itemId));
    setBusy(true); const loadingId = pushLoading();
    try {
      const { data } = await api.post(`${BASE}/submit-batch`, { session_id: sessionId, approved: true });
      const results = data.results || [];
      const created = data.created ?? results.filter((r) => r.success).length;
      resolveLoading(loadingId, data.message || `Submitted ${created}/${results.length} for approval.`,
        results.length && created === results.length ? "success" : "error");
      if (results.length) push({ kind: "bulk_submit_result", results, created, total: results.length });
    } catch (e) { resolveLoading(loadingId, `Batch submit failed: ${e?.response?.data?.detail || e.message}`, "error"); }
    finally { setBusy(false); }
  }
  async function keepEditing() {
    setBusy(true); const loadingId = pushLoading();
    try {
      const { data } = await api.post(`${BASE}/reject`, { session_id: sessionId });
      resolveLoading(loadingId, data.message || "Okay — tell me what to change.");
      setDraft(null);
      setItems((xs) => xs.filter((x) => x.kind !== "confirm"));
    }
    catch { resolveLoading(loadingId, "Okay — tell me what to change."); } finally { setBusy(false); }
  }
  // Quick-action alternate entry point into the SAME inline confirm card used by
  // normal chat: read the already-picked BU from the wizard, ask the backend to
  // analyze similar PRs in it, and — on success — push the returned draft as a
  // "confirm" chat item exactly like awaiting_confirmation does.
  async function createSimilar() {
    const buId = wizardValues.business_unit;
    if (!buId || busy) return;
    setChatStarted(true);
    setBusy(true);
    const loadingId = pushLoading();
    try {
      const { data } = await api.post(`${BASE}/similar-in-bu`, { business_unit_id: buId });
      if (data.status === "ok" && data.draft) {
        resolveLoading(loadingId, data.message || "Based on similar PRs in this Business Unit, here's a draft — review and confirm below.", "success");
        setDraft(data.draft);
        push({ kind: "confirm", draft: data.draft });
      } else {
        resolveLoading(loadingId, data.message || "Couldn't build a draft from similar requisitions in this Business Unit.", "error");
      }
    } catch (e) {
      resolveLoading(loadingId, `Couldn't create a similar PR: ${e?.response?.data?.detail || e.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function clearChat() {
    try { await api.post(`${BASE}/clear`, { session_id: sessionId }); } catch { /* ignore */ }
    setItems([]); setDraft(null); setInput(""); setChatStarted(false);
  }
  async function onFile(e) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || busy) return;
    setChatStarted(true);
    const kind = file.type === "application/pdf" ? "pdf" : file.type.startsWith("image/") ? "image"
      : /\.(xlsx|xls|csv)$/i.test(file.name) ? "excel" : /\.docx?$/i.test(file.name) ? "docx" : "other";
    const url = URL.createObjectURL(file);
    // Whatever the user already typed before attaching is their STATED INTENT for
    // this upload (e.g. "create a new PR from this" / "attach to PR 12345") — the
    // backend skips the create-new/attach-existing question when intent is already
    // clear, so pass it through as the auto-sent message instead of a generic one.
    const statedIntent = input.trim();
    setInput("");
    push({ kind: "chat", role: "user", text: `Attached: ${file.name}` });
    push({ kind: "file_chip", name: file.name, url, fkind: kind });   // chip only — no content dump
    setBusy(true); const loadingId = pushLoading();
    try {
      const form = new FormData(); form.append("session_id", sessionId); form.append("file", file);
      const { data } = await api.post(`${BASE}/upload`, form);
      if (data.kind === "excel") {
        if (data.status === "error") {
          // Unsupported sheet (mixed append/new rows, or nothing recognizable).
          resolveLoading(loadingId, data.message, "error");
        } else if (data.classification === "line_items_only") {
          // Pure line items, no PR header anywhere — no approval needed; hand off
          // to the normal chat turn so the agent collects BU/preparer/description
          // and builds ONE requisition from these lines.
          resolveLoading(loadingId, data.message);
          await send(statedIntent
            || `I've uploaded a spreadsheet with ${data.line_count} line item(s) and no PR header — please build one requisition from them.`);
        } else {
          // line_append | single_pr | single_pr_multi_line | multiple_prs
          push({ kind: "batch", batch: { ...data } });
          resolveLoading(loadingId, data.message);
        }
      } else {
        // PDF / Word: parsed & kept server-side. Show a VERIFICATION card with the
        // parsed highlights and let the user confirm what to do — do not silently
        // auto-proceed (per the "verify important details" requirement).
        if (data.parsed && data.needs_verification) {
          resolveLoading(loadingId, "I've read your document — please check the details below.");
          push({ kind: "parsed_doc", parsed: data.parsed, fileName: file.name, statedIntent });
        } else {
          // No extractable text (scanned image PDF etc.) — fall back to the old flow.
          resolveLoading(loadingId, "Document received.");
          await send(statedIntent || `I've uploaded a document: ${file.name}.`);
        }
      }
    } catch (err) { resolveLoading(loadingId, `Could not process the file: ${err?.response?.data?.detail || err.message}`, "error"); }
    finally { setBusy(false); }
  }
  async function approveBatch(itemId) {
    // Remove the approval card, show a loading buffer, and run synchronously
    // (NOT in the background) — then render every completed creation on screen.
    setItems((xs) => xs.filter((x) => x.id !== itemId));
    setBusy(true); const loadingId = pushLoading();
    try {
      const { data } = await api.post(`${BASE}/batch`, { session_id: sessionId, approved: true });
      const summary = data.summary || {};
      const results = summary.results || [];
      const created = summary.created ?? results.filter((r) => r.success).length;
      resolveLoading(loadingId, data.message || `Batch complete: ${created}/${results.length} created.`,
        created === results.length ? "success" : "error");
      if (results.length) push({ kind: "batch_result", results, created, total: results.length });
    } catch (e) {
      resolveLoading(loadingId, `Batch failed: ${e?.response?.data?.detail || e.message}`, "error");
    } finally { setBusy(false); }
  }
  async function dryRunValidate() {
    setValidating(true); setFieldErrors({}); setFieldWarnings({}); setWizardResult(null);
    try { const { data } = await api.post(`${BASE}/validate`, { field_values: payloadFV() }); setFieldErrors(data.field_errors || {}); setFieldWarnings(data.field_warnings || {}); setWizardResult({ status: data.ok ? "ok" : "error", message: data.message }); }
    catch (e) { setWizardResult({ status: "error", message: e?.response?.data?.detail || e.message }); } finally { setValidating(false); }
  }
  async function createInOracle() {
    setWizardBusy(true); setWizardResult(null); setFieldErrors({}); setFieldWarnings({});
    try {
      const { data } = await api.post(`${BASE}/manual`, payloadFV());
      setWizardResult(data);
      if (data?.field_errors) setFieldErrors(data.field_errors);
      if (data?.field_warnings) setFieldWarnings(data.field_warnings);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = Array.isArray(raw) ? raw.map((x) => `${Array.isArray(x.loc) ? x.loc.join(" → ") : x.loc}: ${x.msg}`).join(" · ") : (raw && typeof raw === "object") ? JSON.stringify(raw) : (raw || e.message);
      setWizardResult({ status: "error", message: msg });
    } finally { setWizardBusy(false); }
  }

  const wizardPanelProps = {
    values: wizardValues, onChange: setWizardValues, fieldErrors, fieldWarnings,
    result: wizardResult, validating, busy: wizardBusy, onValidate: dryRunValidate, onCreate: createInOracle,
    user,
  };

  const ChatStream = (
    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5 bg-gray-50">
      {items.map((it) => {
        if (it.kind === "chat") return <Bubble key={it.id} msg={it} phraseIdx={phraseIdx} />;
        if (it.kind === "confirm") return <div key={it.id} className="pl-8"><ConfirmCard draft={it.draft} busy={busy} onConfirm={confirmSubmit} onKeepEditing={keepEditing} /></div>;
        if (it.kind === "postcreate") return <div key={it.id}><PostCreateCard info={it.info} busy={busy} onApplyAccount={(acct) => applyChargeAccount(acct, it.id)} onSubmit={() => submitForApproval(it.id)} /></div>;
        if (it.kind === "bulk_submit") return <div key={it.id}><BulkSubmitCard picked={it.picked} busy={busy} onApprove={() => approveBulkSubmit(it.id)} onCancel={() => setItems((xs) => xs.filter((x) => x.id !== it.id))} /></div>;
        if (it.kind === "bulk_submit_result") return <div key={it.id}><BulkSubmitResultCard results={it.results} created={it.created} total={it.total} /></div>;
        if (it.kind === "field") return <div key={it.id} className="pl-8"><FieldCard fieldUi={it.fieldUi} disabled={it.answered} onSubmit={(v) => send(v)}
          onSkip={() => setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, answered: true } : x)))}
          onRawPick={(field, row) => onRawPick(field, row, it.id)} lookupContext={lookupContext} /></div>;
        if (it.kind === "add_line_confirm") return <div key={it.id} className="pl-8"><AddLineConfirmCard pending={it.pending} busy={busy}
          onConfirm={() => confirmAddLine(it.id)} onCancel={() => setItems((xs) => xs.filter((x) => x.id !== it.id))} /></div>;
        if (it.kind === "update_confirm") return <div key={it.id} className="pl-8"><UpdateConfirmCard pending={it.pending} busy={busy}
          onConfirm={() => confirmUpdate(it.id)} onCancel={() => setItems((xs) => xs.filter((x) => x.id !== it.id))} /></div>;
        if (it.kind === "parsed_doc") return <div key={it.id} className="pl-8"><ParsedDocCard parsed={it.parsed} fileName={it.fileName} busy={busy}
          onCreateNew={() => { setItems((xs) => xs.filter((x) => x.id !== it.id)); send("Create a new requisition from the document I uploaded."); }}
          onAttachExisting={() => { setItems((xs) => xs.filter((x) => x.id !== it.id)); send("Attach the uploaded document to an existing requisition."); }} /></div>;
        if (it.kind === "pdf") return <div key={it.id} className="pl-8"><PdfCard info={it.info} busy={busy} /></div>;
        if (it.kind === "file_chip") return <div key={it.id} className="flex justify-end"><FileChip name={it.name} url={it.url} fkind={it.fkind} /></div>;
        if (it.kind === "batch") return <div key={it.id} className="pl-8"><BatchCard batch={it.batch} busy={busy} onApprove={() => approveBatch(it.id)} onCancel={() => setItems((xs) => xs.filter((x) => x.id !== it.id))} /></div>;
        if (it.kind === "batch_result") return <div key={it.id} className="pl-8"><BatchResultCard results={it.results} created={it.created} total={it.total} /></div>;
        return null;
      })}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4" onMouseDown={close}>
      <style>{`
        .pr-modal-pre  { width:720px; max-width:calc(100vw - 2rem); max-height:90vh; }
        .pr-modal-chat { width:min(94vw,1200px); height:90vh; }
      `}</style>
      <div onMouseDown={(e) => e.stopPropagation()}
        className={`bg-white text-gray-900 rounded-2xl border border-gray-300 shadow-2xl flex flex-col overflow-hidden ${(chatStarted || view === "dashboard") ? "pr-modal-chat" : "pr-modal-pre"}`}
        style={{ transition: "width .4s cubic-bezier(0.4,0,0.2,1), height .4s cubic-bezier(0.4,0,0.2,1)" }}>

        {/* header */}
        <div className="px-5 py-3.5 border-b border-gray-200 flex items-start justify-between shrink-0 gap-3">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Oracle Fusion ERP · Vaku AI</div>
            <h3 className="text-xl font-black tracking-tight mt-0.5">Purchase Requisition Create</h3>
            <p className="text-xs text-gray-500 mt-0.5">Guided wizard or AI chat — validated against Oracle before submit</p>
          </div>
          {/* Create / Dashboard toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 shrink-0 self-center">
            <button onClick={() => setView("create")}
              className={`px-3 py-1.5 text-[11px] font-semibold rounded-md flex items-center gap-1.5 ${view === "create" ? "bg-white text-[#0020C2] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <ChatCircleText size={13} weight="bold" /> Create
            </button>
            <button onClick={() => setView("dashboard")}
              className={`px-3 py-1.5 text-[11px] font-semibold rounded-md flex items-center gap-1.5 ${view === "dashboard" ? "bg-white text-[#0020C2] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <SquaresFour size={13} weight="bold" /> Dashboard
            </button>
          </div>
          <div className="flex items-center gap-3 shrink-0 self-center">
            {view === "create" && (
              <div className="flex items-center gap-1" title="Strict enforces the funds check before submitting; Free treats funds as advisory only.">
                <span className="text-[10px] text-gray-400 uppercase tracking-wide">Validation</span>
                <div className="flex items-center bg-gray-100 rounded-md p-0.5">
                  <button onClick={() => setValidationMode("strict")}
                    className={`px-2 py-1 text-[10px] font-semibold rounded ${validationMode === "strict" ? "bg-white text-[#0020C2] shadow-sm" : "text-gray-500"}`}>Strict</button>
                  <button onClick={() => setValidationMode("free")}
                    className={`px-2 py-1 text-[10px] font-semibold rounded ${validationMode === "free" ? "bg-white text-[#0020C2] shadow-sm" : "text-gray-500"}`}>Free</button>
                </div>
              </div>
            )}
            <button onClick={() => { setOpenAgent(null); setSetupOpen?.(true); }} className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"><Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}</button>
            {view === "create" && <button onClick={clearChat} title="Clear" className="text-gray-400 hover:text-gray-700"><Trash size={16} /></button>}
            <button onClick={close} title="Close" className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
          </div>
        </div>

        {/* body */}
        {view === "dashboard" ? (
          <PrDashboard />
        ) : !chatStarted ? (
          /* PRE-CHAT — WizardFields centered, with a chat starter below */
          <>
            <div className="flex-1 overflow-y-auto min-h-0">
              <WizardPanel {...wizardPanelProps} compact />
            </div>
            {/* Prominent quick-action — distinct from the low-key QUICK_PROMPTS
                autocomplete dropdown in the Composer below. */}
            <div className="border-t border-gray-200 px-4 py-2.5 bg-white shrink-0">
              <button
                onClick={createSimilar}
                disabled={busy || !wizardValues.business_unit}
                title={!wizardValues.business_unit ? "Pick a Business Unit first" : undefined}
                className="w-full bg-[#0020C2] text-white px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50 rounded flex items-center justify-center gap-1.5">
                <Sparkle size={12} weight="fill" />
                Create similar to existing PRs in this Business Unit
              </button>
            </div>
            <div className="border-t border-gray-200 px-4 py-1.5 bg-gray-50 shrink-0 flex items-center gap-2 text-[11px] text-gray-400">
              <Robot size={12} weight="bold" /><span>Or describe it / paste JSON / attach a file below to use the AI agent instead</span>
            </div>
            <Composer value={input} onChange={setInput} onSend={() => send()} onAttach={() => fileRef.current?.click()} busy={busy}
              placeholder="Describe your requisition, paste a JSON payload, or attach a file…" history={userHistory} />
          </>
        ) : (
          /* CHAT MODE — chat 60% left / wizard 40% right */
          <div className="flex flex-1 overflow-hidden min-h-0 flex-col md:flex-row">
            <div className="flex flex-col overflow-hidden border-b md:border-b-0 md:border-r border-gray-200 md:w-[60%] flex-1 min-h-0">
              {ChatStream}
              {draft && (
                <div className="border-t border-gray-200 bg-gray-50 px-4 py-1.5 text-[11px] text-gray-600 shrink-0">
                  <span className="font-medium text-gray-700">Draft:</span> {draft.lines?.length || 0} line(s) · {draftTotal(draft).sum.toFixed(2)} {draftTotal(draft).ccy} · <span className="text-gray-400">BU</span> {draft.requisitioning_bu_name || draft.requisitioning_bu_id || "—"}
                </div>
              )}
              <Composer value={input} onChange={setInput} onSend={() => send()} onAttach={() => fileRef.current?.click()} busy={busy} placeholder="Continue the conversation…" history={userHistory} />
            </div>
            <div className="md:w-[40%] md:min-w-[360px] bg-white flex flex-col min-h-0">
              <WizardPanel {...wizardPanelProps} />
            </div>
          </div>
        )}

        <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,image/*" onChange={onFile} />
      </div>
    </div>
  );
}
